import { useState } from "react";
import { useSelector } from "react-redux";

import { TableSortLabel } from "@mui/material";
import { Button } from "@mui/material";

import { Tooltip } from "components/Tooltip";
import { ItemSelect } from "components/formFields/ItemSelect";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { inputPropStyles } from "components/formFields/lineItemFieldStyle";
import { ButtonProgress } from "components/utility/ButtonProgress";

import { i18n } from "services/i18nService";
import { getItemQuicklist } from "services/sosInventoryService/sosApi";
import { setPageDirty } from "services/utility/edit";
import {
  checkForUnexpectedProps,
  createItemQuickListFilters,
} from "services/utility/misc";

import { useDynamicOptions } from "hooks/useDynamicOptions";

import { theme } from "SosTheme";
import { ITEM_TYPES } from "appConstants";

const sx = { "& .MuiTableSortLabel-icon": { fontSize: "14px" } };

export function LineInventoryItemHeading(props) {
  const { lineHandler, label = i18n("columns.Item") } = props;
  const [sort, setSort] = useState(false);

  function handleSort() {
    const newSort = sort !== "asc" ? "asc" : "desc";
    setSort(newSort);
    setPageDirty();
    lineHandler({ type: "sort", direction: newSort, sortBy: "name" });
  }

  return (
    <LineTableHeadingCell onClick={handleSort} sx={{ cursor: "pointer" }}>
      <TableSortLabel
        sx={sx}
        active={Boolean(sort)}
        direction={sort ? sort : "asc"}
      >
        {label}
      </TableSortLabel>
    </LineTableHeadingCell>
  );
}

export function LineInventoryItem(props) {
  const {
    line,
    itemsCount,
    items: externalItems,
    addItem,
    onValueChange,
    allowExpand,
    expandItemGroup,
    itemLoading,
    itemFormType: formType,
    itemFetchCase,
    error,
    disabled,
    objectType,
    lineReadOnly = false,
    showReadOnlyDisabledColor = false,
    showReadOnlyTooltip = false,
    ...unexpected
  } = props;
  checkForUnexpectedProps("LineInventoryItem", unexpected);

  async function fetchFn(query) {
    const { data } = await getItemQuicklist({
      ...(itemFetchCase
        ? createItemQuickListFilters(itemFetchCase)
        : { formType }),
      query,
    });
    return data;
  }

  const {
    options: items,
    fetchOnChange: fetchItemsOnChange,
    handleInputChange,
    handleBlur,
  } = useDynamicOptions(externalItems, itemsCount, fetchFn);

  const loadingState = useSelector((state) => state.loading.editModal);
  const linesLoading = useSelector((state) => state.loading.linesLoading);

  // does items contain the item referenced? if not, add it (this can happen if
  // an inventory item was looked up by name, barcode, or SKU, which ignores the
  // "show on purchasing forms" flag)
  let itemsPlus = items ? [...items] : [];
  if (line.item) {
    const foundItem = items?.find(({ id }) => id === line.item.id);
    if (!foundItem) {
      itemsPlus.unshift(line.item);
    }
  }

  // Read-only mode: just display the item name as text
  if (lineReadOnly) {
    const nameDiv = (
      <div
        style={{
          padding: "8px 14px",
          overflow: inputPropStyles.overflow,
          fontSize: inputPropStyles.fontSize,
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: showReadOnlyDisabledColor ? theme.palette.disabled : undefined,
        }}
      >
        {line.item?.name || ""}
      </div>
    );
    return (
      <div style={{ position: "relative" }}>
        {showReadOnlyTooltip ? (
          <Tooltip title={line.item?.name || ""} placement="top-start">
            {nameDiv}
          </Tooltip>
        ) : (
          nameDiv
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <ItemSelect
        disabled={disabled || itemLoading || linesLoading}
        addItem={addItem}
        options={items ? itemsPlus : null}
        value={line.item}
        onValueChange={onValueChange}
        onInputChange={(e, value, reason) =>
          fetchItemsOnChange && handleInputChange(value, reason)
        }
        onBlur={() => fetchItemsOnChange && handleBlur()}
        fetchItemsOnChange={fetchItemsOnChange}
        error={error}
        objectType={objectType}
      />
      {itemLoading && <ButtonProgress dataTesting="itemLoading" />}
      {line.itemDetails?.type === ITEM_TYPES.KIT && allowExpand && (
        <Button
          size="small"
          disabled={loadingState}
          onClick={() => expandItemGroup(line)}
        >
          {i18n("global.Expand")}
        </Button>
      )}
    </div>
  );
}
